import React, { useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { getSinceInstallationLabel } from "../../utils/installationDate";
import { SummaryStatCardsRow } from "../../components/v2/cards";
import LoadingIndicator from "../../components/v2/cards/LoadingIndicator";
import "./Page.css";
import "./GreenMilesV2.css";
import CleanLogo from "../../img/v2/clean-logo.svg";
import LeafIcon from "../../img/v2/leaf-icon.svg";
import WaterIcon from "../../img/v2/water-icon.svg";
import logoSlider from "../../img/v2/Logo_slider.png";
import logoDon from "../../img/v2/Logo_don.png";
import logoBoutique from "../../img/v2/Logo_boutique.png";
import {
  getDemoHotel73GreenMilesStats,
  isDemoHotel73,
} from "../../utils/demoHotel73Stats";

const FRONT_TOKEN = "Bs#PSQyhy)!y9Ti{";

const getActionIcon = (type) => {
  const typeUpper = type?.toUpperCase();
  switch (typeUpper) {
    case "LUNICLEAN":
    case "YESNOMAIL":
      return <img src={CleanLogo} alt="" aria-hidden="true" className="greenmiles-v2__action-icon" />;
    case "LUNISHOWER":
      return <img src={WaterIcon} alt="" aria-hidden="true" className="greenmiles-v2__action-icon" />;
    default:
      return (
        <img
          src={LeafIcon}
          alt=""
          aria-hidden="true"
          className="greenmiles-v2__action-icon greenmiles-v2__action-icon--small"
        />
      );
  }
};

const getRewardLogoSrc = (rwType) => {
  switch (rwType) {
    case "SLIDER":
      return logoSlider;
    case "EAU":
      return logoDon;
    case "CODE":
      return logoBoutique;
    default:
      return null;
  }
};

const getRewardIconColor = (rewardName, rwType) => {
  const name = (rewardName || "").toLowerCase();
  if (name.includes("spa")) return "#91B0D8";
  if (name.includes("boutique") || name.includes("shop")) return "#CFA7A0";
  if (rwType === "EAU") return "#6C8EBF";
  return "#B0C996";
};

const GreenMilesV2 = () => {
  const auth = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!auth?.isAuthenticated || !auth.user?.access_token || !hId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    if (isDemoHotel73(hId)) {
      setStats(getDemoHotel73GreenMilesStats());
      setHasError(false);
      setLoading(false);
      return;
    }

    const url = `/api/hotels/${hId}/greenmiles/stats`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + auth.user.access_token,
        "x-front-token": FRONT_TOKEN,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.success === "OK") {
          setStats(data);
          setHasError(false);
        } else {
          setHasError(true);
          setStats(null);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("GreenMilesV2 - Error fetching stats:", error);
        setHasError(true);
        setStats(null);
        setLoading(false);
      });
  }, [auth?.isAuthenticated, auth.user?.access_token, hId]);

  const summaryCards = useMemo(() => {
    const stays = stats?.summary?.greenMilesStays;
    const usage = stats?.summary?.greenMilesUsage;
    const scanRate = stats?.summary?.greenMilesScanRate;
    const summarySubtitle = getSinceInstallationLabel(stats?.installationDate, lang, locale);
    const isDemo = isDemoHotel73(hId);

    return [
      {
        id: "greenmiles-stays",
        title: translate("v2_greenmiles_users", lang),
        subtitle: summarySubtitle,
        value: loading ? "..." : (stays?.value ?? 0).toLocaleString(locale),
        accentColor: "#3C5544",
        footerText: translate("v2_greenmiles_stays_footer", lang),
        hideDelta: true,
        loading,
      },
      {
        id: "greenmiles-scan-rate",
        title: translate("v2_greenmiles_scan_rate", lang),
        subtitle: summarySubtitle,
        value:
          loading
            ? "..."
            : Number(scanRate?.value ?? 0).toLocaleString(locale, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }),
        valueUnit: "%",
        accentColor: "#3C5544",
        footerText: translate("v2_greenmiles_scan_rate_footer", lang),
        hideDelta: true,
        loading,
        hide: false,
      },
      {
        id: "greenmiles-usage",
        title: translate("v2_greenmiles_usage", lang),
        subtitle: summarySubtitle,
        value: loading ? "..." : Math.round(usage?.value ?? 0).toLocaleString(locale),
        valueUnit: "%",
        accentColor: "#3C5544",
        footerText: translate("v2_greenmiles_usage_footer", lang),
        hideDelta: true,
        loading,
      },
    ];
  }, [loading, stats, lang, locale, hId]);

  const actions = stats?.actions || [];
  const rewards = stats?.rewards || [];

  return (
    <div className="v2-page greenmiles-v2">
      <header className="v2-page__header">
        <h1 className="v2-page__title">{translate("v2_greenmiles_title", lang)}</h1>
      </header>

      {hasError && (
        <div className="greenmiles-v2__error">
          {translate("v2_error_load_data", lang)}
        </div>
      )}

      <div className="v2-page__content greenmiles-v2__content">
        <div className="greenmiles-v2__stats-top">
          <SummaryStatCardsRow cards={summaryCards} />
        </div>

        <section className="greenmiles-v2__section-card">
          <div className="greenmiles-v2__section-header">
            <h2 className="greenmiles-v2__section-title">{translate("v2_greenmiles_actions_title", lang)}</h2>
            <p className="greenmiles-v2__section-subtitle">{translate("v2_greenmiles_actions_subtitle", lang)}</p>
          </div>

          <div className="greenmiles-v2__table">
            <div className="greenmiles-v2__table-header">
              <div>{translate("v2_greenmiles_action_name", lang)}</div>
              <div className="greenmiles-v2__table-header-cell--center">{translate("v2_greenmiles_action_count", lang)}</div>
              <div className="greenmiles-v2__table-header-cell--center">{translate("v2_greenmiles_gm_column", lang)}</div>
            </div>

            {loading ? (
              <div className="greenmiles-v2__table-loading">
                <LoadingIndicator />
              </div>
            ) : actions.length === 0 ? (
              <div className="greenmiles-v2__table-empty">{translate("v2_greenmiles_no_actions", lang)}</div>
            ) : (
              actions.map((action) => (
                <div key={action.id} className="greenmiles-v2__table-row">
                  <div className="greenmiles-v2__table-cell-name">
                    {getActionIcon(action.type)}
                    <span className="greenmiles-v2__table-cell-text">{action.name || "—"}</span>
                  </div>
                  <div className="greenmiles-v2__table-cell-value greenmiles-v2__table-cell--center">
                    {action.nbEffectue ?? 0}
                  </div>
                  <div className="greenmiles-v2__table-cell-value greenmiles-v2__table-cell-value--muted greenmiles-v2__table-cell--center">
                    {action.greenMilesReward ?? 0}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="greenmiles-v2__section-card">
          <div className="greenmiles-v2__section-header">
            <h2 className="greenmiles-v2__section-title">{translate("v2_greenmiles_rewards_title", lang)}</h2>
            <p className="greenmiles-v2__section-subtitle">{translate("v2_greenmiles_rewards_subtitle", lang)}</p>
          </div>

          <div className="greenmiles-v2__table">
            <div className="greenmiles-v2__table-header">
              <div>{translate("v2_greenmiles_reward_name", lang)}</div>
              <div className="greenmiles-v2__table-header-cell--center">{translate("v2_greenmiles_reward_unlocked", lang)}</div>
              <div className="greenmiles-v2__table-header-cell--center">{translate("v2_greenmiles_reward_required", lang)}</div>
            </div>

            {loading ? (
              <div className="greenmiles-v2__table-loading">
                <LoadingIndicator />
              </div>
            ) : rewards.length === 0 ? (
              <div className="greenmiles-v2__table-empty">{translate("v2_greenmiles_no_rewards", lang)}</div>
            ) : (
              rewards.map((reward) => (
                <div key={reward.rwId} className="greenmiles-v2__table-row">
                  <div className="greenmiles-v2__table-cell-name">
                    {getRewardLogoSrc(reward.rwType) ? (
                      <img
                        className="greenmiles-v2__reward-logo"
                        src={getRewardLogoSrc(reward.rwType)}
                        alt=""
                        aria-hidden="true"
                      />
                    ) : (
                      <span
                        className="greenmiles-v2__reward-icon"
                        style={{ backgroundColor: getRewardIconColor(reward.name, reward.rwType) }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="greenmiles-v2__table-cell-text">{reward.name || "—"}</span>
                  </div>
                  <div className="greenmiles-v2__table-cell-value greenmiles-v2__table-cell--center">
                    {reward.unlockedCount ?? 0}
                  </div>
                  <div className="greenmiles-v2__table-cell-value greenmiles-v2__table-cell-value--muted greenmiles-v2__table-cell--center">
                    {reward.greenMilesCost ?? 0}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default GreenMilesV2;
